import React, { useEffect, useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/Layout/Sidebar';
import FormRenderer from './components/Forms/FormRenderer';
import { formConfig } from './config/formConfig';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

// Branded Error Boundary to prevent silent blank screens
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center font-sans">
          <div className="w-20 h-20 mb-8">
            <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-2">Something went wrong.</h1>
          <p className="text-slate-500 mb-8 text-lg">Please refresh or contact support.</p>
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 max-w-lg w-full overflow-auto">
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-2 text-left">Error Details</p>
            <pre className="text-sm text-slate-600 font-mono whitespace-pre-wrap text-left">{this.state.error?.toString()}</pre>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="mt-8 px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg"
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Branded NotFound route
const NotFound = () => {
  const location = useLocation();
  const isPublicMode = location.pathname.startsWith('/f/');
  
  return (
    <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-24 h-24 bg-white p-4 rounded-3xl shadow-xl border border-[var(--border)] flex items-center justify-center mb-10">
        <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
      </div>
      <h1 className="text-5xl font-black text-[var(--text)] mb-3 tracking-tight">BPA Solutions</h1>
      <p className="text-[var(--textMuted)] mb-10 text-xl font-medium">Page not found.</p>
      {!isPublicMode && (
        <Link 
          to="/hub"
          className="px-10 py-4 bg-[var(--primary)] text-white font-black rounded-2xl hover:opacity-90 transition-all shadow-xl shadow-blue-500/20"
        >
          Return to Hub
        </Link>
      )}
    </div>
  );
};

const HubGate = ({ children }) => {
  const [accessKey, setAccessKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const requiredKey = import.meta.env[formConfig.access.hub.accessKeyEnvVar];

  useEffect(() => {
    const stored = localStorage.getItem('bpaHubKey');
    if (!requiredKey || stored === requiredKey) {
      setIsAuthenticated(true);
    }
  }, [requiredKey]);

  const handleAccess = (e) => {
    e.preventDefault();
    if (accessKey === requiredKey) {
      localStorage.setItem('bpaHubKey', accessKey);
      setIsAuthenticated(true);
    } else {
      alert('Invalid access key.');
    }
  };

  if (!isAuthenticated && requiredKey) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-[var(--surface)] p-12 rounded-[40px] border border-[var(--border)] shadow-2xl text-center">
          <div className="w-20 h-20 bg-white p-4 rounded-2xl shadow-sm border border-[var(--border)] flex items-center justify-center mx-auto mb-10">
            <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-4xl font-black text-[var(--text)] mb-2 tracking-tight">BPA Solutions</h1>
          <p className="text-[var(--textMuted)] mb-10 font-bold uppercase tracking-widest text-xs">Internal Forms Hub</p>
          <form onSubmit={handleAccess} className="space-y-4">
            <input
              type="password"
              placeholder="Enter Access Key"
              className="w-full bg-[var(--background)] border border-[var(--border)] rounded-2xl px-6 py-4 text-center text-lg focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
            />
            <button className="w-full py-5 bg-[var(--primary)] text-white font-black rounded-2xl hover:opacity-90 transition-opacity shadow-xl shadow-blue-500/20">
              Access Hub
            </button>
          </form>
        </div>
      </div>
    );
  }

  return children;
};

const PublicShareView = ({ routeId }) => {
  const location = useLocation();
  const route = formConfig.site.routes.find(r => r.id === routeId);
  const form = route ? formConfig.forms.find(f => f.id === route.targetId) : null;
  
  const searchParams = new URLSearchParams(location.search);
  const paramName = formConfig.access.publicShare.queryParam;
  const providedKey = searchParams.get(paramName);

  const BrandingWrap = ({ children }) => (
    <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-20 h-20 bg-white p-4 rounded-2xl shadow-xl border border-[var(--border)] flex items-center justify-center mx-auto mb-8">
        <img src={formConfig.app.branding.logo} alt="BPA" className="w-full h-full object-contain" />
      </div>
      <h2 className="text-3xl font-black text-[var(--text)] mb-6 tracking-tight">BPA Solutions</h2>
      <div className="p-10 bg-[var(--surface)] border border-[var(--border)] rounded-[40px] shadow-2xl w-full max-w-md">
        {children}
      </div>
    </div>
  );

  if (!route || !form) {
    return (
      <BrandingWrap>
        <p className="text-[var(--text)] font-black text-xl mb-2">Form Unavailable</p>
        <p className="text-[var(--textMuted)]">The requested form could not be found.</p>
      </BrandingWrap>
    );
  }

  if (route.shareKey && providedKey !== route.shareKey) {
    return (
      <BrandingWrap>
        <div className="w-16 h-16 bg-[var(--danger)]/10 text-[var(--danger)] rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon name="AlertTriangle" className="text-3xl" />
        </div>
        <p className="text-[var(--text)] font-black text-xl mb-2">Invalid Link</p>
        <p className="text-[var(--textMuted)]">This secure link is invalid or has expired.</p>
      </BrandingWrap>
    );
  }

  return <FormRenderer form={form} isPublicMode={true} route={route} />;
};

const PageRenderer = ({ page }) => (
  <div className="max-w-4xl mx-auto py-12">
    <h2 className="text-6xl font-black text-[var(--text)] mb-12 tracking-tighter">{page.title}</h2>
    <div className="space-y-10">
      {page.blocks.map((block, idx) => {
        if (block.type === 'paragraph') {
          return <p key={idx} className="text-2xl text-[var(--textMuted)] leading-relaxed font-medium">{block.text}</p>;
        }
        if (block.type === 'bullets') {
          return (
            <ul key={idx} className="space-y-6">
              {block.items.map((item, i) => (
                <li key={i} className="flex items-start gap-5 text-xl text-[var(--text)] font-semibold">
                  <div className="mt-2.5 w-2.5 h-2.5 rounded-full bg-[var(--primary)] shrink-0 shadow-lg shadow-blue-500/40" />
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  </div>
);

const AppContent = () => {
  const { toggleTheme, currentThemeId } = useTheme();
  const location = useLocation();
  const isPublicMode = location.pathname.startsWith('/f/');

  // Handle dynamic document titles
  useEffect(() => {
    const path = location.pathname;
    const currentRoute = formConfig.site.routes.find(r => 
      r.path === path || r.sharePath === path
    );

    if (currentRoute) {
      if (currentRoute.type === 'form') {
        const form = formConfig.forms.find(f => f.id === currentRoute.targetId);
        document.title = "BPA Solutions - " + (form ? form.name : "Form");
      } else if (currentRoute.type === 'page') {
        const page = formConfig.site.pages.find(p => p.id === currentRoute.targetId);
        document.title = "BPA Solutions - " + (page ? page.title : "Page");
      }
    } else if (path === '/' || path === '/hub') {
      document.title = "BPA Solutions - Forms Hub";
    } else {
      document.title = "BPA Solutions - Forms";
    }
  }, [location.pathname]);

  const headerBranding = (
    <div className="flex items-center gap-5">
      <div className="w-11 h-11 rounded-2xl bg-white p-2 shadow-sm border border-[var(--border)] flex items-center justify-center">
        <img src={formConfig.app.branding.logo} alt="Logo" className="w-full h-full object-contain" />
      </div>
      <div className="flex items-center gap-4 text-[var(--textMuted)]">
        {!isPublicMode && (
          <>
            <div className="p-2.5 rounded-xl bg-[var(--surfaceAlt)] text-[var(--text)]">
              <SafeIcon name="Home" className="text-sm" />
            </div>
            <div className="w-px h-4 bg-[var(--border)]" />
          </>
        )}
        <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[var(--text)]">BPA Solutions</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--text)] selection:bg-[var(--primarySoft)] selection:text-[var(--primaryStrong)]">
      {!isPublicMode && <Sidebar />}
      <main className={isPublicMode ? "w-full" : "pl-[280px]"}>
        <header className="h-24 border-b border-[var(--border)] flex items-center justify-between px-12 sticky top-0 bg-[var(--background)]/80 backdrop-blur-2xl z-50">
          {headerBranding}
          <div className="flex items-center gap-5">
            {formConfig.themes.themeSwitcher.enabled && (
              <button
                onClick={toggleTheme}
                className="w-12 h-12 rounded-2xl bg-[var(--surfaceAlt)] text-[var(--text)] hover:scale-105 active:scale-95 transition-all flex items-center justify-center shadow-sm border border-[var(--border)]"
              >
                <SafeIcon name={currentThemeId === 'light' ? 'Moon' : 'Sun'} className="text-xl" />
              </button>
            )}
            {!isPublicMode && (
              <div className="w-11 h-11 rounded-2xl bg-[var(--primary)] text-white flex items-center justify-center font-black text-xs shadow-xl shadow-blue-500/30">
                AD
              </div>
            )}
          </div>
        </header>

        <div className={isPublicMode ? "p-12 max-w-[1000px] mx-auto" : "p-12"}>
          <Routes>
            {formConfig.site.routes.map(route => {
              const components = [];
              if (route.type === 'form') {
                const form = formConfig.forms.find(f => f.id === route.targetId);
                components.push(
                  <Route 
                    key={route.id} 
                    path={route.path} 
                    element={<HubGate><FormRenderer form={form} isPublicMode={false} route={route} /></HubGate>} 
                  />
                );
                if (route.sharePath) {
                  components.push(
                    <Route 
                      key={`${route.id}-share`} 
                      path={route.sharePath} 
                      element={<PublicShareView routeId={route.id} />} 
                    />
                  );
                }
              } else if (route.type === 'page') {
                const page = formConfig.site.pages.find(p => p.id === route.targetId);
                components.push(
                  <Route 
                    key={route.id} 
                    path={route.path} 
                    element={<HubGate><PageRenderer page={page} /></HubGate>} 
                  />
                );
              }
              return components;
            })}
            
            <Route path="/hub" element={<HubGate><Navigate to="/website-intake" replace /></HubGate>} />
            <Route path="/" element={<Navigate to="/hub" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;